import { get, patch, post, put } from "./divertidogsApiUtils";
import { useQuery } from "@tanstack/react-query";
import { MascotaResumida } from "./mascotasServicios";
import { TipoServicio } from "./ventasServicios";
import { ValueChange } from "../utils/ValueChange";
import { stringADate } from "../utils/dateUtils";

export interface ItemListadoServiciosTransporte {
  id: string;
  nombre: string;
  descripcion?: string;
  nombreSector?: string;
  precio: number;
}

export const listarServiciosTransporte = async (): Promise<
  ItemListadoServiciosTransporte[] | undefined
> => {
  return get<ItemListadoServiciosTransporte[]>(`servicios/transporte`);
};

export type TipoTrayectoTransporte = "UN_TRAYECTO" | "DOS_TRAYECTOS";
export interface NuevoServicioTransporte {
  nombre: string;
  descripcion?: string;
  idSector?: string;
  precio: number;
  tipoTrayecto: TipoTrayectoTransporte;
}

export const crearServicioTransporte = async (
  nuevoServicioTransporte: NuevoServicioTransporte
): Promise<undefined> => {
  return post<NuevoServicioTransporte, any | undefined>(
    "servicios/transporte",
    nuevoServicioTransporte
  );
};

interface SectorTransporte {
  id: string;
  nombre: string;
}

export interface ServicioTransporte {
  id: string;
  nombre: string;
  descripcion?: string;
  sector?: SectorTransporte;
  precio: number;
  tipoTrayecto: TipoTrayectoTransporte;
}

export const buscarServicioTransporte = async (
  id: string
): Promise<ServicioTransporte | undefined> => {
  return get<ServicioTransporte>(`servicios/transporte/${id}`);
};

export const QUERY_KEY_BUSCAR_SERVICIO_TRANSPORTE =
  "buscar_servicio_transporte";

export const useBuscarServicioTransporte = (id?: string) => {
  if (!id) {
    throw Error("Id de servicio no definido");
  }
  return useQuery(
    [QUERY_KEY_BUSCAR_SERVICIO_TRANSPORTE],
    async () => await buscarServicioTransporte(id)
  );
};

export interface ServicioTransporteModificado {
  id: string;
  nombre: string;
  descripcion?: string;
  idSector?: string;
  precio: number;
  tipoTrayecto: TipoTrayectoTransporte;
}

export const reemplazarServicioTransporte = async (
  servicioTransporte: ServicioTransporteModificado
) => {
  return put<ServicioTransporte, undefined>(
    `servicios/transporte`,
    servicioTransporte
  );
};

export const QUERY_KEY_BUSCAR_PLANES_TRANSPORTE_COMPATIBLES =
  "QUERY_KEY_BUSCAR_PLANES_TRANSPORTE_COMPATIBLES";

export interface ServiciosCompatibles {
  unTrayecto: ServicioTransporte[];
  dosTrayectos: ServicioTransporte[];
}

export const buscarServiciosCompatibles = async (idSector: string) => {
  return get<ServiciosCompatibles>(`servicios/transporte/sector/${idSector}`);
};

export const useBuscarServiciosCompatibles = (
  idSector: string
) => {
  return useQuery({
    queryKey: [QUERY_KEY_BUSCAR_PLANES_TRANSPORTE_COMPATIBLES],
    queryFn: async () => await buscarServiciosCompatibles(idSector)
  });
};

export interface ReservaTransporteGuarderia {
  fechaInicio: Date;
  fechaFin: Date;
  fechasProgramadas: Date[];
  idSector: string;
  direccion: string;
}

export interface ReservaTransporteGuarderiaManual
  extends ReservaTransporteGuarderia {
  precioManual: number;
}

export interface ReservaTransporteGuarderiaPlan
  extends ReservaTransporteGuarderia {
  idServicio: string;
}

export const isReservaTransporteGuarderiaManual = (
  r: ReservaTransporteGuarderia
): r is ReservaTransporteGuarderiaManual => {
  return !!(r as ReservaTransporteGuarderiaManual).precioManual;
};

export const isReservaTransporteGuarderiaPlan = (
  r: ReservaTransporteGuarderia
): r is ReservaTransporteGuarderiaPlan => {
  return !!(r as ReservaTransporteGuarderiaPlan).idServicio;
};

export interface HojaDeTransporte {
  id: string;
  fecha: Date;
  entradas: EntradaTransporte[];
  salidas: SalidaTransporte[];
}

export interface DetalleTransporte {
  id: string;
  mascota: MascotaResumida;
  orden: number;
  propietario: string;
  servicios: TipoServicio[];
  hora: string;
  transportado: boolean;
  direccion: string;
  celular: string;
  sector: string;
  observaciones?: string;
  tipo: "ENTRADA" | "SALIDA";
}

export interface EntradaTransporte extends DetalleTransporte {
  tipo: "ENTRADA";
}

export interface SalidaTransporte extends DetalleTransporte {
  tipo: "SALIDA";
  ingreso: boolean;
}

export function esEntrada(
  detalle: DetalleTransporte
): detalle is EntradaTransporte {
  return (detalle as EntradaTransporte).tipo === "ENTRADA";
}

export function esSalida(
  detalle: DetalleTransporte
): detalle is SalidaTransporte {
  return (detalle as SalidaTransporte).tipo === "SALIDA";
}

export const obtenerHojaDeTransporteDeHoy = async () => {
  return get<HojaDeTransporte>(`transportes/hoy`);
};

export const QUERY_OBTENER_HOJA_DE_TRANSPORTE_DE_HOY =
  "QUERY_OBTENER_HOJA_DE_TRANSPORTE_DE_HOY";

export const useObtenerHojaDeTransporteDeHoy = () => {
  return useQuery(
    [QUERY_OBTENER_HOJA_DE_TRANSPORTE_DE_HOY],
    async () => await obtenerHojaDeTransporteDeHoy()
  );
};

export const existeHojaDeTransporteDeHoy = async () => {
  return get<boolean>(`transportes/hoy/existe`);
};

export const QUERY_EXISTE_HOJA_DE_TRANSPORTE_DE_HOY =
  "QUERY_EXISTE_HOJA_DE_TRANSPORTE_DE_HOY";

export const useExisteHojaDeTransporteDeHoy = () => {
  return useQuery(
    [QUERY_EXISTE_HOJA_DE_TRANSPORTE_DE_HOY],
    async () => await existeHojaDeTransporteDeHoy()
  );
};

export const confirmarRecogidas = async (
  confirmaciones: Map<string, boolean>
) => {
  return patch<Record<string, boolean>>(
    `transportes/confirmar_recogidas`,
    Object.fromEntries(confirmaciones)
  );
};

export const confirmarEntregas = async (
  confirmaciones: Map<string, boolean>
) => {
  return patch<Record<string, boolean>>(
    `transportes/confirmar_entregas`,
    Object.fromEntries(confirmaciones)
  );
};

export interface HojaDeTransportePorFecha {
  id: string;
  fecha: string;
  entradas: EntradaTransporteDeFecha[];
  salidas: SalidaTransporteDeFecha[];
}

export interface DetalleTransporteDeFecha {
  id: string;
  mascota: MascotaResumida;
  propietario: string;
  servicios: TipoServicio[];
  hora: string;
  transportado: boolean;
  direccion: string;
  celular: string;
  sector: { id: string; nombre: string };
  observaciones?: string;
  tipo: "ENTRADA" | "SALIDA";
  orden: number;
}

export interface EntradaTransporteDeFecha extends DetalleTransporteDeFecha {
  tipo: "ENTRADA";
}

export interface SalidaTransporteDeFecha extends DetalleTransporteDeFecha {
  tipo: "SALIDA";
  ingreso: boolean;
}

export function esEntradaDeFecha(
  detalle: DetalleTransporteDeFecha
): detalle is EntradaTransporteDeFecha {
  return (detalle as EntradaTransporteDeFecha).tipo === "ENTRADA";
}

export function esSalidaDeFecha(
  detalle: DetalleTransporteDeFecha
): detalle is SalidaTransporteDeFecha {
  return (detalle as SalidaTransporteDeFecha).tipo === "SALIDA";
}

export const obtenerTransportesPorFecha = async () => {
  const res = await get<Record<string, HojaDeTransportePorFecha>>(
    `transportes/por_fecha`
  );
  let resMap = new Map<Date, HojaDeTransportePorFecha>();
  for (const dateStr in res) {
    let transp = res[dateStr];
    resMap.set(stringADate(transp.fecha), transp);
  }

  return resMap;
};

export const QUERY_OBTENER_TRANSPORTES_POR_FECHA = "QUERY_OBTENER_TRANSPORTES";

export const useObtenerTransportesPorFecha = () => {
  return useQuery(
    [QUERY_OBTENER_TRANSPORTES_POR_FECHA],
    async () => await obtenerTransportesPorFecha()
  );
};

export interface ModificacionesDetalleTransporte {
  idHoja: string;
  id: string;
  tipo: "ENTRADA" | "SALIDA";
  direccion?: ValueChange<string>;
  sector?: ValueChange<string>;
  observaciones?: ValueChange<string | undefined>;
  hora?: ValueChange<string>;
}

export interface ModificacionesHojaDeTransporte {
  id: string;
  entradas: ModificacionesDetalleTransporte[];
  salidas: ModificacionesDetalleTransporte[];
}

export const modificarHojaDeTransporte = async (
  modificarHojaDeTransporte: ModificacionesHojaDeTransporte
) => {
  return patch<ModificacionesHojaDeTransporte>(
    `transportes/modificar`,
    modificarHojaDeTransporte
  );
};

export interface ModificacionesDetalleTransporteDiario {
  id: string;
  orden?: ValueChange<number>;
  transportado?: ValueChange<boolean>;
}

export interface ModificacionesHojaDiaria {
  id: string;
  entradas: ModificacionesDetalleTransporteDiario[];
  salidas: ModificacionesDetalleTransporteDiario[];
}

export const modificarHojaDiaria = async (
  modificaciones: ModificacionesHojaDiaria
) => {
  return patch<ModificacionesHojaDiaria>(`transportes/hoy`, modificaciones);
};

export interface ModificacionesDetalleTransporte {
  idHoja: string;
  id: string;
  tipo: "ENTRADA" | "SALIDA";
  direccion?: ValueChange<string>;
  sector?: ValueChange<string>;
  observaciones?: ValueChange<string | undefined>;
  hora?: ValueChange<string>;
}

type ModificarDetalleTransporteRequest = Omit<
  ModificacionesDetalleTransporte,
  "idHoja"
>;

export const modificarDetalleTransporte = async (
  modificarDetalleTransporte: ModificacionesDetalleTransporte
) => {
  const { id, tipo, direccion, sector, observaciones, hora } =
    modificarDetalleTransporte;
  return patch<ModificarDetalleTransporteRequest>(
    `transportes/${modificarDetalleTransporte.idHoja}`,
    { id, tipo, direccion, sector, observaciones, hora }
  );
};
